import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SmallSpinner, percent, isNumber, Icon } from 'yankees-react-utils';

const StyledBatterStats = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    > table {
        flex: 1;
        margin: 1px;
        tr {
            background-color: transparent !important;
            td {
                padding: 1px !important;
            }
        }
    }
    > div {
        width: 100%;
        text-align: center;
        font-weight: bold;
        margin-top: 1px;
        span + span {
            margin-left: 10px;
        }
    }
`;

const hasPitch = ({ pitchtype }, repertoire) => {
    if (!repertoire?.length) return false;
    return !pitchtype || pitchtype === 1 || repertoire.some((r) => r.FXType === pitchtype);
};
const firstPitchSwColor = (pitch) => {
    let className = '';
    if (isNumber(pitch?.swprediction)) {
        if (pitch.swprediction < 0.16) className = 'darkblue';
        else if (pitch.swprediction > 0.41) className = 'standardred';
        else if (pitch.swprediction > 0.36) className = 'lightred';
        else if (pitch.swprediction < 0.23) className = 'lightblue';
    }
    return className;
};
const firstPitchChColor = (pitch) => {
    let className = '';
    if (isNumber(pitch?.chprediction)) {
        if (pitch.chprediction > 0.41) className = 'standardred';
        else if (pitch.chprediction > 0.36) className = 'lightred';
        else if (pitch.chprediction < 0.21) className = 'darkblue';
        else if (pitch.chprediction < 0.26) className = 'lightblue';
    }
    return className;
};
const firstPitchMsColor = (pitch) => {
    let className = '';
    if (isNumber(pitch?.msprediction)) {
        if (pitch.msprediction > 0.35) className = 'standardred';
        else if (pitch.msprediction > 0.29) className = 'lightred';
        else if (pitch.msprediction < 0.17) className = 'darkblue';
        else if (pitch.msprediction < 0.23) className = 'lightblue';
    }
    return className;
};

const CatcherWristbandBatterStats = ({ repertoire, rispExpand, firstPitches }) => {
    const { t: translate } = useTranslation();
    if (!firstPitches || !rispExpand) return <SmallSpinner />;

    const firstPitchTypeStats = firstPitches
        .filter((p) => hasPitch(p, repertoire))
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((pitch) => (
            <tr key={pitch.pitchTypeName}>
                <td><b>{pitch.pitchTypeName}</b>{pitch.split && <>&nbsp;{pitch.split}</>}</td>
                <td className={firstPitchSwColor(pitch)}>{percent(pitch.swprediction)}</td>
                <td>/</td>
                <td className={firstPitchChColor(pitch)}>{percent(pitch.chprediction)}</td>
                <td>/</td>
                <td className={firstPitchMsColor(pitch)}>{percent(pitch.msprediction)}</td>
            </tr>
        ));
    return (
        <StyledBatterStats>
            {repertoire.length > 4 && (
                <>
                    <table>
                        <tbody>
                            {firstPitchTypeStats.filter((_, idx) => idx < (firstPitchTypeStats.length / 2))}
                        </tbody>
                    </table>
                    <table>
                        <tbody>
                            {firstPitchTypeStats.filter((_, idx) => idx >= (firstPitchTypeStats.length / 2))}
                        </tbody>
                    </table>
                    {rispExpand && (rispExpand.expand === 1 || !!rispExpand.ciflag_atk || !!rispExpand.ciflag_2k || !!rispExpand.hbpindicator) && (
                        <div>
                            {rispExpand.expand === 1 && <span>{translate('sections.opponent.team.risp-expand')}</span>}
                            {!!rispExpand.ciflag_atk && <span className={`ciflag${rispExpand.ciflag_atk}`}>{translate('sections.opponent.team.abbr-catcher-interfearance')} ({translate('sections.opponent.team.abbr-attack')}).</span>}
                            {!!rispExpand.ciflag_2k && <span className={`ciflag${rispExpand.ciflag_2k}`}>{translate('sections.opponent.team.abbr-catcher-interfearance')} ({translate('sections.opponent.team.abbr-two-strike')}).</span>}
                            {!!rispExpand.hbpindicator && <span className={`hbpflag${rispExpand.hbpindicator}`}><Icon icon="certificate" color={rispExpand.hbpindicator === 2 ? '#FF0000' : '#FFA500'} /></span>}
                        </div>
                    )}
                </>
            )}
            {repertoire.length <= 4 && (
                <>
                    <table>
                        <tbody>
                            {firstPitchTypeStats}
                        </tbody>
                    </table>
                </>
            )}
        </StyledBatterStats>
    );
};

CatcherWristbandBatterStats.propTypes = {
    repertoire: PropTypes.arrayOf(PropTypes.shape({
        FXType: PropTypes.number.isRequired,
    })).isRequired,
    rispExpand: PropTypes.shape({
        expand: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
        // eslint-disable-next-line camelcase
        ciflag_atk: PropTypes.number,
        // eslint-disable-next-line camelcase
        ciflag_2k: PropTypes.number,
        hbpindicator: PropTypes.number,
    }),
    firstPitches: PropTypes.arrayOf(PropTypes.shape({
        pitchtype: PropTypes.number,
        pitchTypeName: PropTypes.string.isRequired,
        sortOrder: PropTypes.number.isRequired,
        chprediction: PropTypes.number,
        swprediction: PropTypes.number,
        msprediction: PropTypes.number,
        split: PropTypes.string,
    })),
};
CatcherWristbandBatterStats.defaultProps = {
    rispExpand: undefined,
    firstPitches: undefined,
};

export default CatcherWristbandBatterStats;
